import { AddOutlined } from '@mui/icons-material';
import { Button, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import DocumentPreview from 'components/v2/common/documentPreview';

import DocumentTemplateFormDialog from './documentTemplateFormDialog';
import DocumentTemplatesList, { type DocumentTemplateRowAction } from './documentTemplatesList';
import ListFiltersBar from './listFiltersBar';
import ScopeToolbar from './scopeToolbar';

import api from 'api';
import { ACTIONS } from 'constant';
import { Role } from 'data/constants/roles';
import { useDebouncedValue } from 'hooks/useCheckEmail';
import useModalObj from 'hooks/useModalObj';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { AppDispatch } from 'redux/store';
import type { DocumentType } from 'types';
import type {
    DocumentTemplateRecord,
    DocumentTemplateScopeFilter,
    OwnershipScope,
    ProviderOption,
    ServiceCountryCode,
} from 'types/servicesDocuments';
import { localizedTemplateName } from 'utils/servicesDocuments';
import { getErrorMessage } from 'utils/v2/errorHelper';

interface Props {
    userRole: string;
    providerOptions: ProviderOption[];
    providersLoading: boolean;
    fixedProviderId: number | null;
    fixedProviderLabel?: string;
}

const DocumentTemplatesTabPanel = ({
    userRole,
    providerOptions,
    providersLoading,
    fixedProviderId,
    fixedProviderLabel,
}: Props) => {
    const dispatch = useDispatch<AppDispatch>();
    const { translate, language } = useTranslate();
    const isSuperSuperAdmin = userRole === Role.SUPER_SUPER_ADMIN;

    const [ownershipScope, setOwnershipScope] = useState<OwnershipScope>('provider');
    const [providerId, setProviderId] = useState<number | ''>(fixedProviderId ?? '');
    const [countryCode, setCountryCode] = useState<ServiceCountryCode | ''>('');
    const [documentType, setDocumentType] = useState<DocumentType | ''>('');
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebouncedValue(search, 300);
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState<DocumentTemplateRecord[]>([]);
    const [preview, setPreview] = useState<{ url: string; name: string } | null>(null);

    const { modalObject, setModalObject, onResetModalObj } = useModalObj<DocumentTemplateRecord>();

    useEffect(() => {
        if (!isSuperSuperAdmin && fixedProviderId != null) {
            setProviderId(fixedProviderId);
            setOwnershipScope('provider');
        }
    }, [fixedProviderId, isSuperSuperAdmin]);

    const effectiveProviderId = useMemo(() => {
        if (!isSuperSuperAdmin) {
            return fixedProviderId ?? undefined;
        }
        if (ownershipScope === 'universal') {
            return undefined;
        }
        return providerId === '' ? undefined : Number(providerId);
    }, [fixedProviderId, isSuperSuperAdmin, ownershipScope, providerId]);

    const listScope: DocumentTemplateScopeFilter = useMemo(
        () => (ownershipScope === 'universal' ? 'universal' : 'provider'),
        [ownershipScope]
    );

    const loadTemplates = useCallback(async () => {
        if (listScope === 'provider' && effectiveProviderId == null) {
            setTemplates([]);
            return;
        }
        setLoading(true);
        try {
            const response = await api.documentTemplates.listDocumentTemplates({
                scope: listScope,
                ...(countryCode ? { country_code: countryCode } : {}),
                ...(documentType ? { document_type: documentType as DocumentType } : {}),
                ...(effectiveProviderId != null
                    ? { service_provider_id: effectiveProviderId }
                    : {}),
            });
            let rows = response.data || [];
            const query = debouncedSearch.trim().toLowerCase();
            if (query) {
                rows = rows.filter((row) => {
                    const haystack = [row.name_en, row.name_fr, row.category, row.document_type]
                        .filter(Boolean)
                        .join(' ')
                        .toLowerCase();
                    return haystack.includes(query);
                });
            }
            setTemplates(rows);
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(
                        error,
                        translate('servicesDocuments.templates.loadFailed')
                    ),
                    type: 'error',
                })
            );
        } finally {
            setLoading(false);
        }
    }, [
        countryCode,
        debouncedSearch,
        dispatch,
        documentType,
        effectiveProviderId,
        listScope,
        translate,
    ]);

    useEffect(() => {
        void loadTemplates();
    }, [loadTemplates]);

    const handlePreview = async (template: DocumentTemplateRecord) => {
        try {
            const response = await api.documentTemplates.getDocumentTemplatePreviewUrl(template.id);
            const url = (response.data as { url?: string })?.url;
            if (!url) {
                throw new Error('Missing preview URL');
            }
            setPreview({
                url,
                name: localizedTemplateName(template, language),
            });
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        }
    };

    const handleRowAction = (
        action: DocumentTemplateRowAction,
        template: DocumentTemplateRecord
    ) => {
        if (action === ACTIONS.PREVIEW_FILE) {
            void handlePreview(template);
            return;
        }
        if (action === ACTIONS.EDIT) {
            setModalObject((prev) => ({
                ...prev,
                action: ACTIONS.EDIT,
                data: template,
                title: 'editDocumentTemplate',
            }));
            return;
        }
        setModalObject((prev) => ({
            ...prev,
            action: ACTIONS.DELETE,
            data: template,
            title: 'delete',
            content: localizedTemplateName(template, language),
        }));
    };

    const handleDelete = async () => {
        const template = modalObject.data;
        if (!template?.id) {
            return;
        }
        try {
            setModalObject((prev) => ({ ...prev, loading: true }));
            await api.documentTemplates.deleteDocumentTemplate(template.id);
            dispatch(setAlert({ message: translate('saveChanges'), type: 'success' }));
            onResetModalObj();
            await loadTemplates();
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
            setModalObject((prev) => ({ ...prev, loading: false }));
        }
    };

    return (
        <Stack spacing={2}>
            <ScopeToolbar
                isSuperSuperAdmin={isSuperSuperAdmin}
                ownershipScope={ownershipScope}
                onOwnershipScopeChange={setOwnershipScope}
                providerId={providerId}
                onProviderIdChange={setProviderId}
                providerOptions={providerOptions}
                providersLoading={providersLoading}
                fixedProviderLabel={fixedProviderLabel}
            />

            <ListFiltersBar
                search={search}
                onSearchChange={setSearch}
                action={
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddOutlined />}
                        onClick={() =>
                            setModalObject((prev) => ({
                                ...prev,
                                action: ACTIONS.CREATE,
                                data: null,
                                title: 'addDocumentTemplate',
                            }))
                        }
                        sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}
                    >
                        {translate('addDocumentTemplate')}
                    </Button>
                }
            />

            <DocumentTemplatesList
                loading={loading}
                templates={templates}
                userRole={userRole}
                currentProviderId={fixedProviderId}
                language={language}
                countryCode={countryCode}
                onCountryChange={setCountryCode}
                documentType={documentType}
                onDocumentTypeChange={setDocumentType}
                handleAction={handleRowAction}
            />

            {modalObject.action === ACTIONS.CREATE || modalObject.action === ACTIONS.EDIT ? (
                <DocumentTemplateFormDialog
                    open
                    onClose={onResetModalObj}
                    onSuccess={loadTemplates}
                    initialData={modalObject.data}
                    userRole={userRole}
                    defaultScope={ownershipScope}
                    defaultProviderId={providerId}
                    providerOptions={providerOptions}
                />
            ) : null}

            {modalObject.action === ACTIONS.DELETE ? (
                <ConfirmationDialog
                    open
                    title={translate('delete')}
                    content={modalObject.content}
                    loading={modalObject.loading}
                    onClose={onResetModalObj}
                    onConfirm={() => void handleDelete()}
                />
            ) : null}

            {preview ? (
                <DocumentPreview
                    open
                    mobile={false}
                    fileUrl={preview.url}
                    fileName={preview.name}
                    onClose={() => setPreview(null)}
                />
            ) : null}
        </Stack>
    );
};

export default DocumentTemplatesTabPanel;
